/**
 * Устройства заведения: кассы, кухня, телефоны официантов, пересчёт.
 *
 * Владелец должен решать сам и за минуту: планшет разбили — перевыпустить
 * код, украли — отвязать, купили второй — добавить. Без звонка кому-либо.
 *
 * Здесь же заканчивается путь дополнительного устройства: платформа
 * одобрила заявку, создала устройство с кодом — и человек должен сразу
 * увидеть, что код ждёт ввода, а плата за это устройство уже идёт.
 * Пока код не введён, заведение платит за то, чем не пользуется.
 *
 * Лимит устройств задаёт тариф. Когда он исчерпан, экран не просто
 * отказывает, а объясняет: сколько занято, сколько положено и к кому
 * идти за расширением.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API = '/api/v1/terminals';

type Kind = 'POS' | 'KDS' | 'WAITER' | 'COUNT';

type Device = {
  id: string; name: string; kind: Kind;
  activationCode: string | null;
  activatedAt: string | null;
  lastSeenAt: string | null;
  isActive: boolean;
  /** Когда устройство завели — приходит, если сервер умеет. */
  createdAt?: string | null;
};

type Limits = { maxPos: number | null; maxKds: number | null; maxWaiter: number | null };

/** Строка счёта за этот вид устройства — из подписки, тем же ключом. */
type BillLine = { kind: string; title: string; qty: number; unitPrice: number; sum: number };

const KIND_RU: Record<Kind, string> = {
  POS: 'Касса', KDS: 'Экран кухни', WAITER: 'Телефон официанта', COUNT: 'Пересчёт',
};

/** Куда идти с этим устройством — человек не обязан помнить адреса. */
const KIND_PATH: Record<Kind, string> = {
  POS: '/pos/', KDS: '/kds/', WAITER: '/waiter/', COUNT: '/count/',
};

const when = (s: string | null | undefined) =>
  (s ? new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

/** Сколько дней устройство ждёт кода. null — сервер не сказал, когда его завели. */
function waitingDays(d: Device): number | null {
  if (!d.createdAt) return null;
  const ms = Date.now() - new Date(d.createdAt).getTime();
  return Math.floor(ms / 86_400_000);
}

async function call<T>(path: string, token: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null) as { code?: string; used?: number; limit?: number } | null;
    if (d?.code === 'DEVICE_LIMIT') {
      throw new Error(`Занято ${d.used} из ${d.limit} по тарифу. Расширение оформляет тот, кто вас подключал.`);
    }
    throw new Error('Не получилось. Попробуйте ещё раз');
  }
  return res.json() as Promise<T>;
}

/**
 * Лист подтверждения вместо системного окошка.
 *
 * Отвязать и убрать — необратимо, поэтому последствие показано словами
 * до нажатия, а не в сером окне браузера без названия заведения.
 */
function Ask({ title, effects, note, danger, confirmLabel, onYes, onNo }: {
  title: string;
  effects: [string, string][];
  note?: string;
  danger?: boolean;
  confirmLabel: string;
  onYes: () => void;
  onNo: () => void;
}) {
  return (
    <div className="dialog" onMouseDown={(e) => e.target === e.currentTarget && onNo()}>
      <div className="dialog-card" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        <dl className="dev-effects">
          {effects.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
        {note && <p className="hint">{note}</p>}
        <div className="modal-actions">
          <button className="btn" onClick={onNo}>Отмена</button>
          <button className={`btn ${danger ? 'danger-solid' : 'primary'}`} onClick={onYes}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Код активации: его диктуют по телефону или набирают на планшете. */
function Code({ value, big }: { value: string; big?: boolean }) {
  const [done, setDone] = useState(false);
  return (
    <span className={`dev-code ${big ? 'big' : ''}`}>
      <b>{value}</b>
      <button
        className="btn small"
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1600);
        }}
      >{done ? 'Скопировано' : 'Скопировать'}</button>
    </span>
  );
}

export function DevicesScreen({ token, locationId }: { token: string; locationId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<Kind>('POS');
  const [fresh, setFresh] = useState<{ name: string; code: string; kind: Kind } | null>(null);
  const [ask, setAsk] = useState<{ what: 'reissue' | 'revoke' | 'remove'; dev: Device } | null>(null);
  const [err, setErr] = useState('');

  const list = useQuery({
    queryKey: ['devices', locationId],
    queryFn: () => call<{ rows: Device[]; limits: Limits }>(`?locationId=${locationId}`, token),
    refetchInterval: 60_000,
  });

  /* Счёт — тем же ключом, что экран «Подписка»: лишнего обращения нет,
     а цифры на обоих экранах совпадают по определению. */
  const bill = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const r = await fetch('/api/v1/subscription/status', { headers: { Authorization: `Bearer ${token}` } });
      return r.json() as Promise<{ lines?: BillLine[] }>;
    },
  });
  const priceOf = (kind: Kind): BillLine | null =>
    (bill.data?.lines ?? []).find((l) => l.kind === kind) ?? null;

  const add = useMutation({
    mutationFn: () => call<{ id: string; code: string }>('', token, {
      method: 'POST', body: { locationId, name: name.trim(), kind },
    }),
    onSuccess: (r) => {
      setFresh({ name: name.trim(), code: r.code, kind });
      setAdding(false); setName(''); setErr('');
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const reissue = useMutation({
    mutationFn: (d: Device) => call<{ code: string }>('/reissue', token, {
      method: 'POST', body: { terminalId: d.id },
    }),
    onSuccess: (r, d) => {
      setFresh({ name: d.name, code: r.code, kind: d.kind });
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => call('/revoke', token, { method: 'POST', body: { terminalId: id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => call('/remove', token, { method: 'POST', body: { terminalId: id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });

  if (list.isPending) return <p className="hint">Загружаем устройства…</p>;
  const rows = list.data?.rows ?? [];
  const lim = list.data?.limits;

  const used = (k: Kind) => rows.filter((r) => r.kind === k && r.isActive).length;
  const limitOf = (k: Kind) =>
    (k === 'POS' ? lim?.maxPos : k === 'KDS' ? lim?.maxKds : k === 'WAITER' ? lim?.maxWaiter : null);

  /* Первое устройство каждого вида входит в тариф, остальные оплачиваются. */
  const paidOne = (d: Device): boolean => {
    if (d.kind === 'COUNT') return false;
    const same = rows.filter((r) => r.kind === d.kind);
    return same.findIndex((r) => r.id === d.id) > 0;
  };

  const waiting = rows.filter((d) => d.activationCode && !d.activatedAt);

  return (
    <section className="menu-cat">
      <header>
        <h2>Устройства</h2>
        <button className="btn primary" onClick={() => { setAdding(true); setErr(''); }}>
          + Устройство
        </button>
      </header>

      {/* Одобренное платформой устройство приходит сюда — и должно броситься в глаза */}
      {waiting.length > 0 && (
        <div className="dev-waiting">
          <b>
            {waiting.length === 1
              ? 'Одно устройство ждёт активации'
              : `${waiting.length} устройства ждут активации`}
          </b>
          <p>
            Код вводят на самом планшете или телефоне при первом запуске — откройте
            на нём адрес устройства и наберите код. Пока это не сделано, устройство
            не работает, а плата за него уже идёт: смотрите раздел «Подписка».
          </p>
          {waiting.some((w) => priceOf(w.kind)) && (
            <p className="dev-waiting-money">
              {waiting.filter((w) => priceOf(w.kind)).map((w) => {
                const line = priceOf(w.kind) as BillLine;
                return `«${w.name}» — ${Math.round(line.unitPrice / 100).toLocaleString('ru-RU')} ₸/мес`;
              }).join(' · ')} — со следующего счёта.
            </p>
          )}
          <div className="dev-waiting-list">
            {waiting.map((d) => {
              const days = waitingDays(d);
              return (
                <div className="dev-waiting-row" key={d.id}>
                  <div>
                    <b>{d.name}</b>
                    <span className="sub">
                      {KIND_RU[d.kind]} · открывается по адресу {KIND_PATH[d.kind]}
                      {days !== null && days >= 7 && ` · ждёт ${days} дн.`}
                    </span>
                  </div>
                  <Code value={d.activationCode as string} big />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="cards">
        {(['POS', 'KDS', 'WAITER'] as Kind[]).map((k) => {
          const l = limitOf(k);
          return (
            <div key={k} className="card">
              <span>{KIND_RU[k]}</span>
              <b>{used(k)}{l ? ` из ${l}` : ''}</b>
            </div>
          );
        })}
      </div>

      <p className="hint dev-money">
        Одно устройство каждого вида входит в тариф, остальные добавляются в счёт —
        он виден в разделе «Подписка». Считаются устройства, а не люди: сотрудников
        заводите сколько нужно, они входят по своим PIN хоть по очереди на одном планшете.
      </p>

      <table className="grid">
        <thead>
          <tr>
            <th>Название</th><th>Тип</th><th>Состояние</th>
            <th>Последняя связь</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const days = waitingDays(d);
            const stale = d.activationCode && !d.activatedAt && days !== null && days >= 7;
            return (
              <tr key={d.id} className={d.isActive ? '' : 'off'}>
                <td>
                  <b>{d.name}</b>
                  <div className="sub">
                    открывается по адресу {KIND_PATH[d.kind]}
                    {paidOne(d) && (() => {
                      const line = priceOf(d.kind);
                      return line
                        ? ` · сверх тарифа, ${Math.round(line.unitPrice / 100).toLocaleString('ru-RU')} ₸/мес`
                        : ' · оплачивается отдельно';
                    })()}
                    {!paidOne(d) && d.kind !== 'COUNT' && ' · входит в тариф'}
                  </div>
                </td>
                <td>{KIND_RU[d.kind]}</td>
                <td>
                  {d.activationCode ? (
                    <>
                      <span className={`badge ${stale ? 'st-expired' : 'st-setup'}`}>
                        {stale ? `ждёт активации ${days} дн.` : 'ждёт активации'}
                      </span>
                      <Code value={d.activationCode} />
                      {stale && paidOne(d) && (
                        <div className="sub bad">плата идёт, а устройство не работает</div>
                      )}
                    </>
                  ) : d.activatedAt ? (
                    <>
                      <span className="badge st-active">привязано</span>
                      <div className="sub">{when(d.activatedAt)}</div>
                    </>
                  ) : (
                    <span className="badge st-suspended">отвязано</span>
                  )}
                </td>
                <td>{when(d.lastSeenAt)}</td>
                <td className="actions">
                  <button className="btn small" onClick={() => setAsk({ what: 'reissue', dev: d })}>
                    Новый код
                  </button>
                  {d.activatedAt && (
                    <button className="btn small ghost" onClick={() => setAsk({ what: 'revoke', dev: d })}>
                      Отвязать
                    </button>
                  )}
                  <button className="btn small ghost" onClick={() => setAsk({ what: 'remove', dev: d })}>
                    Убрать
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="note">
              Устройств пока нет. Нажмите «+ Устройство» и получите код —
              его вводят один раз на планшете или телефоне.
            </td></tr>
          )}
        </tbody>
      </table>

      {ask?.what === 'reissue' && (
        <Ask
          title={`Выдать новый код для «${ask.dev.name}»`}
          effects={[
            ['Устройство', `${KIND_RU[ask.dev.kind]} · ${ask.dev.name}`],
            ['Старый код', 'перестанет работать сразу'],
            ['Что делать дальше', `открыть ${KIND_PATH[ask.dev.kind]} на новом устройстве и ввести код`],
          ]}
          note="Нужно, если планшет сломался, потерян или продан. Место по тарифу останется занятым тем же устройством."
          confirmLabel="Выдать новый код"
          onNo={() => setAsk(null)}
          onYes={() => { reissue.mutate(ask.dev); setAsk(null); }}
        />
      )}

      {ask?.what === 'revoke' && (
        <Ask
          title={`Отвязать «${ask.dev.name}»`}
          effects={[
            ['Устройство', `${KIND_RU[ask.dev.kind]} · ${ask.dev.name}`],
            ['Работать с него', 'станет нельзя сразу'],
            ['Новый код', 'не выдаётся — устройство останется в списке'],
            ['Плата', paidOne(ask.dev) ? 'продолжит идти, пока устройство в списке' : 'входит в тариф'],
          ]}
          note="Подходит, если замены пока нет. Чтобы освободить место по тарифу, устройство нужно убрать."
          danger
          confirmLabel="Отвязать"
          onNo={() => setAsk(null)}
          onYes={() => { revoke.mutate(ask.dev.id); setAsk(null); }}
        />
      )}

      {ask?.what === 'remove' && (
        <Ask
          title={`Убрать «${ask.dev.name}» совсем`}
          effects={[
            ['Устройство', `${KIND_RU[ask.dev.kind]} · ${ask.dev.name}`],
            ['Место по тарифу', 'освободится'],
            ['История чеков', 'сохранится'],
            ['Плата', paidOne(ask.dev) ? 'за это устройство прекратится' : 'не изменится — оно входит в тариф'],
          ]}
          danger
          confirmLabel="Убрать устройство"
          onNo={() => setAsk(null)}
          onYes={() => { remove.mutate(ask.dev.id); setAsk(null); }}
        />
      )}

      {adding && (
        <div className="dialog">
          <div className="dialog-card">
            <h2>Новое устройство</h2>
            <label>Название
              <input value={name} placeholder="Касса у входа"
                onChange={(e) => setName(e.target.value)} />
            </label>
            <label>Тип
              <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                {(['POS', 'KDS', 'WAITER', 'COUNT'] as Kind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_RU[k]}{limitOf(k) ? ` · занято ${used(k)} из ${limitOf(k)}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <p className="hint">
              Первое устройство каждого вида входит в тариф. Если свободных мест
              не осталось, попросите расширение у того, кто вас подключал.
            </p>
            {err && <p className="rb-warn">{err}</p>}
            <div className="modal-actions">
              <button className="btn" onClick={() => setAdding(false)}>Отмена</button>
              <button className="btn primary" disabled={name.trim().length < 2 || add.isPending}
                onClick={() => add.mutate()}>Создать и получить код</button>
            </div>
          </div>
        </div>
      )}

      {fresh && (
        <div className="dialog">
          <div className="dialog-card">
            <h2>Код для «{fresh.name}»</h2>
            <p className="hint">
              Откройте на самом устройстве адрес <b>{KIND_PATH[fresh.kind]}</b> и введите код
              при первом запуске. Он работает один раз — второе устройство по нему не подключится.
            </p>
            <div className="dev-fresh"><Code value={fresh.code} big /></div>
            <div className="modal-actions">
              <button className="btn primary" onClick={() => setFresh(null)}>Готово</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
